import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { DragIndicator, Save, Search } from "@mui/icons-material";
import { TextField, Button, IconButton, Paper, Alert, Pagination, Box, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import "./PropertyOrder.css";

const PropertyOrder = () => {
  const [properties, setProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [changedProperties, setChangedProperties] = useState(new Map());
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const user = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    fetchProperties();
  }, []);

  // Filter and paginate properties
  const filteredProperties = useMemo(() => {
    return properties.filter(property =>
      property.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [properties, searchTerm]);

  const paginatedProperties = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredProperties
      .sort((a, b) => a.order - b.order)
      .slice(startIndex, endIndex);
  }, [filteredProperties, currentPage, itemsPerPage]);

  const totalPages = itemsPerPage >= filteredProperties.length ? 1 : Math.ceil(filteredProperties.length / itemsPerPage);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(
        "http://localhost:8080/property/getPropertiesNamesWithOrder",
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      // Ensure each property has an order value based on its index
      const propertiesWithOrder = response.data.propertiesNames.map(
        (property, index) => ({
          ...property,
          order: property.order || index + 1,
        })
      );
      setProperties(propertiesWithOrder);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  };

  const handleOrderChange = (propertyId, newOrder) => {
    const parsedOrder = parseInt(newOrder) || 0;
    setProperties((prevProperties) =>
      prevProperties.map((property) =>
        property._id === propertyId
          ? { ...property, order: parsedOrder }
          : property
      )
    );
    setChangedProperties((prev) => new Map(prev).set(propertyId, parsedOrder));
    setHasChanges(true);
  };

  const handleOrderBlur = () => {
    const sortedProperties = [...properties].sort((a, b) => a.order - b.order);
    const reorderedProperties = sortedProperties.map((property, index) => {
      const newOrder = index + 1;
      if (property.order !== newOrder) {
        setChangedProperties((prev) =>
          new Map(prev).set(property._id, newOrder)
        );
      }
      return { ...property, order: newOrder };
    });
    setProperties(reorderedProperties);
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setError(null);
      setSuccess(null);

      const updatePromises = Array.from(changedProperties.entries()).map(
        ([propertyId, newOrder]) =>
          axios.put(
            `http://localhost:8080/property/changePropertyOrder/${propertyId}`,
            { newOrder },
            {
              headers: {
                Authorization: `Bearer ${user.token}`,
              },
            }
          )
      );

      await Promise.all(updatePromises);
      setChangedProperties(new Map());
      setHasChanges(false);
      await fetchProperties();
      setSuccess("Property orders updated successfully");
    } catch (error) {
      console.error("Failed to save order changes:", error);
      setError("Failed to update property orders. Please try again.");
    }
  };

  const handleDragEnd = useCallback((result) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;
    
    // Calculate actual indices in the full array
    const startIndex = (currentPage - 1) * itemsPerPage;
    const sourceGlobalIndex = startIndex + sourceIndex;
    const destinationGlobalIndex = startIndex + destinationIndex;

    // Create new array with reordered items
    const newProperties = [...filteredProperties];
    const [reorderedItem] = newProperties.splice(sourceGlobalIndex, 1);
    newProperties.splice(destinationGlobalIndex, 0, reorderedItem);

    // Update orders for affected items only
    const updatedProperties = [...properties];
    newProperties.forEach((item, index) => {
      const newOrder = index + 1;
      const propertyIndex = updatedProperties.findIndex(p => p._id === item._id);
      if (propertyIndex !== -1 && updatedProperties[propertyIndex].order !== newOrder) {
        updatedProperties[propertyIndex] = { ...updatedProperties[propertyIndex], order: newOrder };
        setChangedProperties((prev) => new Map(prev).set(item._id, newOrder));
      }
    });

    setProperties(updatedProperties);
    setHasChanges(true);
  }, [currentPage, itemsPerPage, filteredProperties, properties]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="property-order-container">
      <div className="top-container">
        <div className="search-and-controls">
          <TextField
            placeholder="Search properties..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            size="small"
            className="search-input"
            InputProps={{
              startAdornment: <Search />
            }}
          />
          <FormControl size="small" className="items-per-page-control">
            <InputLabel>Items per page</InputLabel>
            <Select
              value={itemsPerPage}
              label="Items per page"
              onChange={(e) => {
                setItemsPerPage(e.target.value);
                setCurrentPage(1); // Reset to first page when changing items per page
              }}
            >
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
              <MenuItem value={200}>200</MenuItem>
              <MenuItem value={filteredProperties.length}>All</MenuItem>
            </Select>
          </FormControl>
        </div>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSave}
          className="save-button"
          disabled={!hasChanges}
        >
          Save Changes
        </Button>
      </div>

      <div className="alerts-container">
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      </div>

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>
          Showing {itemsPerPage >= filteredProperties.length ? 'all' : `${Math.min(itemsPerPage, filteredProperties.length)}`} of {filteredProperties.length} properties
          {searchTerm && ` (filtered from ${properties.length} total)`}
        </span>
        {totalPages > 1 && (
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, page) => setCurrentPage(page)}
            color="primary"
          />
        )}
      </Box>

      <div className="scroll-container">
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="properties">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {paginatedProperties.map((property, index) => (
                  <Draggable
                    key={property._id}
                    draggableId={property._id}
                    index={index}
                  >
                    {(provided) => (
                      <Paper
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className="property-order-item"
                      >
                        <div {...provided.dragHandleProps}>
                          <IconButton className="drag-handle">
                            <DragIndicator />
                          </IconButton>
                        </div>
                        <TextField
                          type="number"
                          value={property.order}
                          onChange={(e) =>
                            handleOrderChange(property._id, e.target.value)
                          }
                          onBlur={handleOrderBlur}
                          className="order-input"
                          size="small"
                        />
                        <span className="property-name">{property.name}</span>
                      </Paper>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    </div>
  );
};

export default PropertyOrder;
